/**
 * Pedagogical reader.
 *
 * Layout: programme in the left sidebar, chapter in the centre, chapter outline
 * on the right. Progress is sent to the API (never kept in the browser only),
 * and the chapter is printable.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle2,
  Clock,
  FlaskConical,
  ListChecks,
  ListTree,
  Printer,
  Target,
} from 'lucide-react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { MarkdownView } from '@/components/content/MarkdownView';
import type { TocEntry } from '@/components/content/markdown';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { FullPageSpinner } from '@/components/ui/Spinner';
import { cn } from '@/lib/cn';
import { formatMinutes } from '@/lib/format';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { api } from '@/services/api';
import { fetchers, queryKeys } from '@/services/queries';

export function CourseReaderPage() {
  const { moduleCode = '', chapterSlug = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [toc, setToc] = useState<TocEntry[]>([]);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const openedAt = useRef(Date.now());

  const module = useQuery({
    queryKey: queryKeys.module(moduleCode),
    queryFn: () => fetchers.module(moduleCode),
    retry: false,
  });
  const chapter = useQuery({
    queryKey: queryKeys.chapter(moduleCode, chapterSlug),
    queryFn: () => fetchers.chapter(moduleCode, chapterSlug),
    retry: false,
    enabled: Boolean(moduleCode && chapterSlug),
  });

  const markComplete = useMutation({
    mutationFn: () =>
      api.put(`/modules/${moduleCode}/chapters/${chapterSlug}/progress`, {
        status: 'completed',
        seconds_spent: Math.min(Math.round((Date.now() - openedAt.current) / 1000), 86_400),
        scroll_percent: 100,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard });
      void queryClient.invalidateQueries({ queryKey: queryKeys.progress });
    },
  });

  // A visit is recorded as "in progress" as soon as the chapter opens, so the
  // dashboard reflects reality even if the learner never clicks the button.
  useEffect(() => {
    openedAt.current = Date.now();
    setToc([]);
    setActiveHeading(null);
    window.scrollTo({ top: 0, behavior: 'auto' });
    if (!moduleCode || !chapterSlug) return;
    void api
      .put(`/modules/${moduleCode}/chapters/${chapterSlug}/progress`, {
        status: 'in_progress',
        seconds_spent: 0,
        scroll_percent: 0,
      })
      .catch(() => {
        // A failed progress ping must never block reading.
      });
  }, [moduleCode, chapterSlug]);

  // Highlight the outline entry matching the heading currently on screen.
  useEffect(() => {
    if (toc.length === 0) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveHeading(visible[0].target.id);
      },
      { rootMargin: '-96px 0px -70% 0px', threshold: 0 },
    );
    toc.forEach((entry) => {
      const element = document.getElementById(entry.id);
      if (element) observer.observe(element);
    });
    return () => observer.disconnect();
  }, [toc]);

  const chapters = useMemo(() => module.data?.chapters ?? [], [module.data]);
  const currentIndex = chapters.findIndex((item) => item.slug === chapterSlug);
  const previous = currentIndex > 0 ? chapters[currentIndex - 1] : undefined;
  const next = currentIndex >= 0 ? chapters[currentIndex + 1] : undefined;
  const readingProgress =
    chapters.length > 0 ? Math.round(((currentIndex + 1) / chapters.length) * 100) : 0;

  const handleToc = useCallback((entries: TocEntry[]) => setToc(entries), []);

  if (module.isLoading || chapter.isLoading) return <FullPageSpinner label="Chargement du cours…" />;
  if (module.isError || chapter.isError || !module.data || !chapter.data) return <NotFoundPage />;

  const handleNext = () => {
    markComplete.mutate();
    if (next) navigate(`/app/modules/${moduleCode}/chapitres/${next.slug}`);
  };

  return (
    <div className="-mx-4 -my-6 lg:-mx-8 lg:-my-8">
      {/* Reading progress bar */}
      <div
        className="kha-no-print sticky top-16 z-20 h-0.5 bg-surface-2"
        role="progressbar"
        aria-valuenow={readingProgress}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Avancement dans le module"
      >
        <div
          className="h-full bg-gradient-to-r from-tech to-ai transition-[width] duration-500"
          style={{ width: `${readingProgress}%` }}
        />
      </div>

      <div className="mx-auto flex max-w-[100rem] gap-8 px-4 py-6 lg:px-8">
        {/* ------------------------------------------------- Programme sidebar */}
        <nav
          aria-label="Programme du module"
          className="kha-no-print sticky top-24 hidden h-[calc(100vh-8rem)] w-64 shrink-0
                     overflow-y-auto xl:block"
        >
          <Link
            to={`/modules/${moduleCode}`}
            className="inline-flex items-center gap-1.5 text-xs font-medium text-muted hover:text-fg"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Fiche du module
          </Link>
          <p className="mt-4 font-mono text-xs font-bold text-tech">{module.data.code}</p>
          <p className="mt-1 text-sm font-bold leading-snug text-fg">{module.data.title}</p>

          <h2 className="mt-6 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-faint">
            <BookOpen className="h-3 w-3" />
            Chapitres
          </h2>
          <ol className="mt-2 space-y-0.5">
            {chapters.map((item, index) => (
              <li key={item.id}>
                <Link
                  to={`/app/modules/${moduleCode}/chapitres/${item.slug}`}
                  aria-current={item.slug === chapterSlug ? 'page' : undefined}
                  className={cn(
                    'flex gap-2.5 rounded-md px-2.5 py-2 text-xs transition-colors',
                    item.slug === chapterSlug
                      ? 'bg-tech/12 font-semibold text-tech'
                      : 'text-muted hover:bg-surface-2 hover:text-fg',
                  )}
                >
                  <span className="shrink-0 tabular-nums opacity-60">{index + 1}.</span>
                  <span className="min-w-0 flex-1">{item.title}</span>
                </Link>
              </li>
            ))}
          </ol>

          {module.data.labs.length > 0 ? (
            <>
              <h2 className="mt-6 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-faint">
                <FlaskConical className="h-3 w-3" />
                Travaux pratiques
              </h2>
              <ul className="mt-2 space-y-0.5">
                {module.data.labs.map((lab) => (
                  <li key={lab.id}>
                    <Link
                      to={`/app/tp/${lab.id}`}
                      className="block rounded-md px-2.5 py-2 text-xs text-muted
                                 hover:bg-surface-2 hover:text-fg"
                    >
                      {lab.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}

          {module.data.quizzes.length > 0 ? (
            <>
              <h2 className="mt-6 flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-faint">
                <ListChecks className="h-3 w-3" />
                Évaluations
              </h2>
              <ul className="mt-2 space-y-0.5">
                {module.data.quizzes.map((quiz) => (
                  <li key={quiz.id}>
                    <Link
                      to={`/app/quiz/${quiz.id}`}
                      className="block rounded-md px-2.5 py-2 text-xs text-muted
                                 hover:bg-surface-2 hover:text-fg"
                    >
                      {quiz.title}
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </nav>

        {/* ------------------------------------------------------- Chapter body */}
        <article className="min-w-0 flex-1">
          <header className="mb-8">
            <div className="kha-no-print flex flex-wrap items-center gap-2">
              <Badge tone="tech">{module.data.code}</Badge>
              <Badge tone="neutral">
                Chapitre {currentIndex + 1}/{chapters.length}
              </Badge>
              <Badge tone="neutral">
                <Clock className="h-3 w-3" />
                {formatMinutes(chapter.data.estimated_minutes)}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                onClick={() => window.print()}
                title="Imprimer ce chapitre"
              >
                <Printer className="h-3.5 w-3.5" />
                Imprimer
              </Button>
            </div>

            <h1 className="mt-4 text-3xl font-extrabold tracking-tight">{chapter.data.title}</h1>
            {chapter.data.subtitle ? (
              <p className="mt-2 text-lg text-muted">{chapter.data.subtitle}</p>
            ) : null}

            {chapter.data.objectives.length > 0 ? (
              <Card className="mt-6 border-tech/25 bg-tech/[0.05]">
                <CardBody>
                  <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-tech">
                    <Target className="h-3.5 w-3.5" />
                    À l'issue de ce chapitre
                  </p>
                  <ul className="mt-3 space-y-1.5">
                    {chapter.data.objectives.map((objective) => (
                      <li key={objective} className="flex gap-2 text-sm text-fg/90">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-tech" aria-hidden="true" />
                        {objective}
                      </li>
                    ))}
                  </ul>
                </CardBody>
              </Card>
            ) : null}
            {/* On a phone both sidebars are hidden, so the programme and the
                outline are reachable here instead — otherwise there would be no
                way to jump to another chapter. */}
            <details className="kha-no-print mt-6 rounded-lg border border-border bg-surface xl:hidden">
              <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-fg">
                Programme et sommaire
              </summary>
              <div className="border-t border-border px-4 py-3">
                <h2 className="text-2xs font-semibold uppercase tracking-wide text-faint">
                  Chapitres
                </h2>
                <ol className="mt-2 space-y-0.5">
                  {chapters.map((item, index) => (
                    <li key={item.id}>
                      <Link
                        to={`/app/modules/${moduleCode}/chapitres/${item.slug}`}
                        aria-current={item.slug === chapterSlug ? 'page' : undefined}
                        className={cn(
                          'flex gap-2 rounded-md px-2 py-1.5 text-xs',
                          item.slug === chapterSlug
                            ? 'bg-tech/12 font-semibold text-tech'
                            : 'text-muted',
                        )}
                      >
                        <span className="shrink-0 tabular-nums opacity-60">{index + 1}.</span>
                        <span className="min-w-0 flex-1">{item.title}</span>
                      </Link>
                    </li>
                  ))}
                </ol>

                {toc.length > 0 ? (
                  <>
                    <h2 className="mt-4 text-2xs font-semibold uppercase tracking-wide text-faint">
                      Sommaire
                    </h2>
                    <ul className="mt-2 space-y-0.5">
                      {toc.map((entry) => (
                        <li key={entry.id}>
                          <a
                            href={`#${entry.id}`}
                            className={cn(
                              'block rounded-md px-2 py-1.5 text-xs text-muted',
                              entry.level === 3 && 'pl-5',
                            )}
                          >
                            {entry.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            </details>
          </header>

          <MarkdownView source={chapter.data.content_markdown} onToc={handleToc} />

          {markComplete.isSuccess ? (
            <Alert tone="success" className="kha-no-print mt-10">
              Progression enregistrée. Votre tableau de bord est à jour.
            </Alert>
          ) : null}

          {/* Previous / next navigation */}
          <nav
            aria-label="Navigation entre chapitres"
            className="kha-no-print mt-12 flex flex-wrap items-center justify-between gap-4
                       border-t border-border pt-6"
          >
            {previous ? (
              <Link
                to={`/app/modules/${moduleCode}/chapitres/${previous.slug}`}
                className="group flex max-w-[45%] items-center gap-2 text-sm text-muted hover:text-fg"
              >
                <ArrowLeft className="h-4 w-4 shrink-0 transition-transform group-hover:-translate-x-0.5" />
                <span className="min-w-0">
                  <span className="block text-2xs uppercase tracking-wide text-faint">Précédent</span>
                  <span className="block truncate font-medium">{previous.title}</span>
                </span>
              </Link>
            ) : (
              <span />
            )}

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={() => markComplete.mutate()}
                disabled={markComplete.isPending || markComplete.isSuccess}
              >
                {markComplete.isSuccess ? (
                  <Check className="h-4 w-4 text-ok" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {markComplete.isSuccess ? 'Chapitre terminé' : 'Marquer comme terminé'}
              </Button>
              {next ? (
                <Button onClick={handleNext}>
                  Suivant
                  <ArrowRight className="h-4 w-4" />
                </Button>
              ) : module.data.quizzes[0] ? (
                <Link
                  to={`/app/quiz/${module.data.quizzes[0].id}`}
                  onClick={() => markComplete.mutate()}
                  className="inline-flex h-10 items-center gap-2 rounded-lg bg-gradient-to-r
                             from-tech to-ai px-4 text-sm font-semibold text-white"
                >
                  Passer le quiz
                  <ArrowRight className="h-4 w-4" />
                </Link>
              ) : null}
            </div>
          </nav>
        </article>

        {/* --------------------------------------------------- Chapter outline */}
        <aside
          aria-label="Sommaire du chapitre (colonne latérale)"
          className="kha-no-print sticky top-24 hidden h-[calc(100vh-8rem)] w-56 shrink-0
                     overflow-y-auto lg:block"
        >
          <h2 className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-faint">
            <ListTree className="h-3 w-3" />
            Sommaire
          </h2>
          {toc.length === 0 ? (
            <p className="mt-3 text-xs text-faint">Aucune section détectée.</p>
          ) : (
            <ul className="mt-3 space-y-0.5 border-l border-border">
              {toc.map((entry) => (
                <li key={entry.id}>
                  <a
                    href={`#${entry.id}`}
                    className={cn(
                      '-ml-px block border-l-2 py-1.5 pl-3 text-xs transition-colors',
                      entry.level === 3 && 'pl-6',
                      activeHeading === entry.id
                        ? 'border-tech font-semibold text-tech'
                        : 'border-transparent text-muted hover:border-faint hover:text-fg',
                    )}
                  >
                    {entry.text}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </div>
  );
}
